/**
 * A entrada do Portal do Trabalhador.
 *
 * Deliberadamente fora do chassi do sistema (`PageShell`, menu, cabeçalho): quem chega aqui não
 * é usuário da clínica, não tem menu para navegar e não deve ver a marca do produto como se
 * fosse cliente dele. É uma tela só, com uma pergunta só.
 *
 * A mensagem de retorno é sempre a mesma — CPF que existe e CPF que não existe recebem a mesma
 * frase, porque a alternativa é oferecer na internet um verificador de quem trabalha onde. A
 * tela não pode "ajudar" dizendo "CPF não encontrado", por mais que isso pareça melhor de usar.
 */

import React, { useCallback, useState } from 'react';
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import portalService from '../../services/portalService';

/** 000.000.000-00 conforme digita. */
function formatarCpf(bruto: string): string {
  const d = bruto.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

export const PortalEntradaPage: React.FC = () => {
  const [cpf, setCpf] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const digitos = cpf.replace(/\D/g, '');

  const enviar = useCallback(async () => {
    if (digitos.length !== 11) {
      setErro('Digite os 11 números do CPF.');
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      setAviso(await portalService.solicitarAcesso(digitos));
    } catch {
      setErro('Não foi possível pedir o link agora. Tente de novo em alguns minutos.');
    } finally {
      setEnviando(false);
    }
  }, [digitos]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper variant="outlined" sx={{ p: 4, width: '100%', maxWidth: 420 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Portal do Trabalhador
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Veja seus exames, treinamentos e atestados.
            </Typography>
          </Box>

          {aviso && (
            <Alert severity="success">
              {aviso}
              <br />
              O link vale por 20 minutos e abre uma vez só.
            </Alert>
          )}
          {erro && <Alert severity="error">{erro}</Alert>}

          <TextField
            label="Seu CPF"
            value={cpf}
            onChange={(e) => setCpf(formatarCpf(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void enviar();
            }}
            placeholder="000.000.000-00"
            fullWidth
            autoFocus
            inputMode="numeric"
            disabled={enviando}
          />

          <Button
            variant="contained"
            size="large"
            onClick={() => void enviar()}
            disabled={enviando || digitos.length !== 11}
          >
            {enviando ? 'Enviando…' : 'Receber link de acesso'}
          </Button>

          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Enviamos por WhatsApp ou e-mail para o contato que a sua empresa cadastrou. Não
            precisa de senha.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default PortalEntradaPage;
